import axios from 'axios';
/* eslint-disable no-use-before-define */
import {
  listPaClientPolicies, listPaClientQuotes, listPaAgentQuotes, isPaOpenQuote,
} from '@/services/agentPaApi';
import {
  listTravelClientPolicies, listTravelClientQuotes, listTravelAgentQuotes, isTravelOpenQuote,
} from '@/services/agentTravelApi';
import fetchHealthPolicies from '@/services/healthPolicyApi';
import { productLabel } from '@/utils/healthPolicyDisplay';
import { fetchMotorPolicyRecords, motorCoverLabel } from '@/utils/motorPolicyList';
import { getCognitoIdentityProfile } from '@/utils/paChannel';
import {
  buildPolicyPreview,
  healthRecencyKey,
  motorRecencyKey,
  myPoliciesRoute,
  paRecencyKey,
  travelRecencyKey,
} from '@/utils/policyRecency';
import { loadPaPendingQuote } from '@/utils/saleCache';

const CUSTOM_AGENT_CODE = 'custom:agent_code';
/** Fetch enough rows per source so global recency sort can surface the newest policies. */
const POLICY_SOURCE_FETCH_LIMIT = 25;
const CLIENT_VALIDATE_PATH = '/customer/gis/clients/validateclient';

const requestConfig = {
  _skipErrorRedirect: true,
  _showErrorToast: false,
};

const silentValidateStatus = {
  validateStatus: () => true,
  _skipErrorRedirect: true,
  _showErrorToast: false,
};

export function formatPolicyNo(value) {
  if (!value) return '—';
  return String(value).replace(/-/g, '/');
}

function identityFromUser(user) {
  return getCognitoIdentityProfile(user);
}

function getUserEmail(user) {
  return user?.email;
}

function getUserKraPin(user) {
  return user?.['custom:kra-pin'];
}

export async function fetchCustomerClient(user) {
  const kraPin = getUserKraPin(user);
  const email = getUserEmail(user);
  if (!kraPin || !email) return { client: null, clientList: [] };
  try {
    const response = await axios.get(
      `${CLIENT_VALIDATE_PATH}?pin=${kraPin}&email=${email}`,
      requestConfig,
    );
    const obj = response.data?.object;
    if (!obj) return { client: null, clientList: [] };
    if (Array.isArray(obj)) {
      return { client: obj[0] || null, clientList: obj };
    }
    return { client: obj, clientList: [obj] };
  } catch {
    return { client: null, clientList: [] };
  }
}

export async function fetchCustomerPolicyRows(user, clientOrPromise, onProgress) {
  const nationalId = user?.['custom:id_number'];
  const email = getUserEmail(user);
  const report = (patch) => {
    if (onProgress) onProgress(patch);
  };

  // Domestic Package listing is paused. Preview is one newest policy per product.
  // Each source reports as soon as it returns so the dashboard can leave the shimmer.
  const [motor, pa, travel, health] = await Promise.all([
    (async () => {
      const client = await Promise.resolve(clientOrPromise).catch(() => null);
      return fetchMotorPolicyRows(client, nationalId, (rows) => report({ motor: rows }));
    })(),
    fetchPaPolicyRows({ nationalId, email }).then((rows) => {
      report({ pa: rows });
      return rows;
    }),
    fetchTravelPolicyRows(email).then((rows) => {
      report({ travel: rows });
      return rows;
    }),
    fetchHealthPolicyRows(nationalId).then((rows) => {
      report({ health: rows });
      return rows;
    }),
  ]);

  return {
    motor,
    pa,
    travel,
    health,
    dp: [],
    preview: buildPolicyPreview(motor, pa, travel, health),
  };
}

async function fetchMotorPolicyRows(client, nationalId, onUpdate) {
  const policies = await fetchMotorPolicyRecords({
    client,
    nationalId,
    requestConfig,
    limit: POLICY_SOURCE_FETCH_LIMIT,
    onUpdate,
  });

  return policies.map((policy) => ({
    id: `motor-${policy.policyNo}`,
    title: motorCoverLabel(policy.coverType),
    meta: `Policy No. ${formatPolicyNo(policy.policyNo)}`,
    icon: 'mdi-car-outline',
    to: myPoliciesRoute('motor', policy),
    sortKey: motorRecencyKey(policy),
  }));
}

async function fetchPaPolicyRows({ nationalId, email }) {
  if (!nationalId && !email) return [];
  try {
    const res = await listPaClientPolicies({ nationalId, email });
    if (!res.success || !Array.isArray(res.data)) return [];
    return res.data.map((policy) => ({
      id: `pa-${policy.quoteId || policy.policyNumber}`,
      title: policy.optionName ? `Students PA · ${policy.optionName}` : 'Students PA',
      meta: `Policy No. ${formatPolicyNo(policy.policyNumber)}`,
      icon: 'mdi-shield-account-outline',
      to: myPoliciesRoute('pa', policy),
      sortKey: paRecencyKey(policy),
    }));
  } catch {
    return [];
  }
}

async function fetchHealthPolicyRows(nationalId) {
  if (!nationalId) return [];
  try {
    const policies = await fetchHealthPolicies(nationalId, requestConfig);
    return policies.map((policy) => ({
      id: `health-${policy.policyNo}`,
      title: productLabel(policy.productName),
      meta: `Policy No. ${formatPolicyNo(policy.policyNo)}`,
      icon: 'mdi-hospital-box-outline',
      to: myPoliciesRoute('health', policy),
      sortKey: healthRecencyKey(policy),
    }));
  } catch {
    return [];
  }
}

async function fetchTravelPolicyRows(email) {
  if (!email) return [];
  try {
    const res = await listTravelClientPolicies({ email });
    if (!res.success || !Array.isArray(res.data)) return [];
    return res.data.map((policy) => {
      const label = policy.region || policy.destinationCountry || 'Travel';
      const policyNo = policy.tqPolicyNumber || policy.mapfrePolicyNumber;
      return {
        id: `travel-${policy.quoteId || policyNo}`,
        title: `Travel · ${label}`,
        meta: policyNo ? `Policy No. ${formatPolicyNo(policyNo)}` : 'Travel Insurance',
        icon: 'mdi-airplane',
        to: myPoliciesRoute('travel', policy),
        sortKey: travelRecencyKey(policy),
      };
    });
  } catch {
    return [];
  }
}

function mergePendingPaQuote(email, paQuotes) {
  const pending = loadPaPendingQuote(email, { liveOnly: true });
  if (!pending?.quoteId || !isPaOpenQuote(pending)) return paQuotes;
  const exists = paQuotes.some((q) => String(q.quoteId) === String(pending.quoteId));
  if (exists) return paQuotes;
  return [pending, ...paQuotes];
}

export async function fetchCustomerQuotes(user) {
  const identity = identityFromUser(user);
  const email = identity?.email || getUserEmail(user);
  const nationalId = identity?.idNumber || user?.['custom:id_number'];
  const kraPin = identity?.kraPin || getUserKraPin(user);

  const [health, dp, travel, pa, motor] = await Promise.all([
    fetchHealthQuotes(email),
    fetchDpQuotes(email),
    fetchTravelQuotes(email),
    fetchPaQuotes({ nationalId, email }),
    kraPin ? fetchMotorQuotes(kraPin) : Promise.resolve([]),
  ]);

  return {
    motor,
    pa: mergePendingPaQuote(email, pa),
    health,
    dp,
    travel,
  };
}

async function fetchMotorQuotes(kraPin) {
  try {
    const url = `/customer/quotation/clientQuotes/${encodeURIComponent(kraPin)}?renewal=false&page=1&size=10`;
    const response = await axios.get(url, requestConfig);
    return response.data?.success ? (response.data.data || []) : [];
  } catch {
    return [];
  }
}

async function fetchPaQuotes({ nationalId, email }) {
  try {
    const res = await listPaClientQuotes({ nationalId, email });
    return res.success && Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

async function fetchHealthQuotes(email) {
  if (!email) return [];
  try {
    const response = await axios.get(
      `/customer/digital-quotation/pacis-amani/quotes?email_address=${encodeURIComponent(email)}`,
      requestConfig,
    );
    return response.data || [];
  } catch {
    return [];
  }
}

async function fetchDpQuotes(email) {
  if (!email) return [];
  try {
    const response = await axios.get(
      `/customer/digital-quotation/domestic-package/quotes?email_address=${encodeURIComponent(email)}`,
      requestConfig,
    );
    return response.data || [];
  } catch {
    return [];
  }
}

async function fetchTravelQuotes(email) {
  if (!email) return [];
  try {
    const res = await listTravelClientQuotes({ email });
    return res.success && Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function fetchNotificationCount(user) {
  const email = getUserEmail(user);
  const kraPin = getUserKraPin(user);
  if (!email || !kraPin) return {};
  try {
    const response = await axios.get(
      `/customer/notification/app/unread/count/${email}?pin=${kraPin}`,
      requestConfig,
    );
    return response.data?.data || {};
  } catch {
    return {};
  }
}

const defaultAgentSnapshot = () => ({
  receiptedSales: 0,
  earnedCommissions: 0,
  commissionsPayable: 0,
  pendingRenewals: 0,
  activeQuotes: 0,
  activeQuotesValue: 0,
  closedSales: 0,
  closedSalesValue: 0,
});

const emptyAgentQuotes = () => ({
  motor: [],
  pa: [],
  health: [],
  dp: [],
  travel: [],
});

export async function fetchAgentDashboardBundle(user) {
  const agnCode = user?.[CUSTOM_AGENT_CODE];
  const email = getUserEmail(user);
  if (!agnCode && !email) {
    return {
      agentNotFound: false,
      summary: null,
      agentNumericId: null,
      agentShortDesc: null,
      conversions: [],
      activeQuotes: [],
      quotes: emptyAgentQuotes(),
      snapshot: defaultAgentSnapshot(),
    };
  }

  const summaryResult = await fetchAgentSummary({ agnCode, email });
  if (summaryResult.agentNotFound) {
    return {
      agentNotFound: true,
      summary: null,
      agentNumericId: null,
      agentShortDesc: null,
      conversions: [],
      activeQuotes: [],
      quotes: emptyAgentQuotes(),
      snapshot: defaultAgentSnapshot(),
    };
  }

  const {
    summary, agentNumericId, agentShortDesc, snapshot,
  } = summaryResult;

  const [conversions, quotesBundle, commissionsSnapshot] = await Promise.all([
    fetchAgentConversions({ agnCode, email }),
    fetchAgentQuotes(user, { agentNumericId, agentShortDesc }),
    fetchAgentCommissionsPayable({ user, summary }),
  ]);

  const activeQuotes = mapQuotesToDashboardActiveQuotes(quotesBundle);

  return {
    agentNotFound: false,
    summary,
    agentNumericId,
    agentShortDesc,
    conversions,
    activeQuotes: activeQuotes.quotes,
    quotes: quotesBundle,
    snapshot: {
      ...snapshot,
      ...commissionsSnapshot,
      activeQuotes: activeQuotes.totalCount,
      activeQuotesValue: activeQuotes.totalValue,
    },
  };
}

async function fetchAgentSummary({ agnCode, email }) {
  const baseUrl = '/customer/gis/agent/dashboard/summary';
  const url = agnCode ? `${baseUrl}?agn_code=${agnCode}` : `${baseUrl}?email=${email}`;
  try {
    const res = await axios.get(url, silentValidateStatus);
    if (res.data?.error === 'Agent not found') {
      return { agentNotFound: true };
    }
    if (res.status !== 200 || !res.data) {
      return {
        agentNotFound: false, summary: null, agentNumericId: null, agentShortDesc: null, snapshot: defaultAgentSnapshot(),
      };
    }

    const summary = res.data;
    let agentNumericId = null;
    let agentShortDesc = null;
    const numericCode = summary.agent?.code;
    if (numericCode != null) {
      const parsed = parseInt(String(numericCode), 10);
      if (!Number.isNaN(parsed)) agentNumericId = parsed;
    }
    agentShortDesc = summary.shortDesc || summary.agent?.shortDesc || summary.summary?.shortDesc || null;

    const s = summary.summary || {};
    const cs = s.closedSales || {};
    const snapshot = {
      ...defaultAgentSnapshot(),
      receiptedSales: s.receiptedSalesMtd ?? 0,
      earnedCommissions: s.earnedCommissions ?? 0,
      commissionsPayable: s.commissionsPayable ?? 0,
      pendingRenewals: s.pendingRenewals ?? 0,
      closedSales: cs.countMtd ?? 0,
      closedSalesValue: cs.valueMtd ?? 0,
    };

    return {
      agentNotFound: false, summary, agentNumericId, agentShortDesc, snapshot,
    };
  } catch {
    return {
      agentNotFound: false, summary: null, agentNumericId: null, agentShortDesc: null, snapshot: defaultAgentSnapshot(),
    };
  }
}

async function fetchAgentConversions({ agnCode, email }) {
  try {
    const baseUrl = '/customer/gis/agent/conversions';
    const params = 'limit=10&portalTransaction=N';
    const url = agnCode
      ? `${baseUrl}?agn_code=${agnCode}&${params}`
      : `${baseUrl}?email=${email}&${params}`;
    const res = await axios.get(url, silentValidateStatus);
    if (res.status !== 200 || !res.data) return [];
    const raw = res.data?.object?.conversions ?? res.data?.conversions ?? res.data?.object ?? res.data?.data;
    const list = Array.isArray(raw) ? raw : [];
    const sorted = [...list].sort((a, b) => (b.coverFrom || '').localeCompare(a.coverFrom || ''));
    return sorted.map((c) => ({
      client: c.clientName ?? c.client ?? '',
      product: c.product ?? '',
      value: c.premium ?? c.value ?? 0,
      policy: c.policyNo ?? c.policy ?? null,
      coverFrom: c.coverFrom ?? null,
      coverTo: c.coverTo ?? null,
      renewalDate: c.renewalDate ?? null,
      batchNo: c.batchNo ?? null,
      currency: c.currency ?? 'KSHS',
    }));
  } catch {
    return [];
  }
}

async function fetchMotorAgentQuotes(agentId) {
  if (!agentId) return [];
  try {
    const url = `/customer/quotation/agent/agentQuotes/${encodeURIComponent(agentId)}?renewal=false&page=1&size=50`;
    const res = await axios.get(url, silentValidateStatus);
    if (res.status !== 200 || !res.data) return [];
    const list = res.data?.data?.content ?? res.data?.content ?? res.data?.data ?? [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

async function fetchPaAgentQuotes(agentId, email) {
  let list = [];
  try {
    const res = await listPaAgentQuotes(agentId, 0, 50);
    const body = res?.data ?? res;
    const rows = body?.content ?? body?.data?.content ?? body?.data ?? body;
    if (Array.isArray(rows) && rows.length > 0) {
      list = rows;
    }
  } catch {
    list = [];
  }
  if (!list.length && email) {
    try {
      const clientRes = await listPaClientQuotes({ email });
      if (Array.isArray(clientRes?.data)) list = clientRes.data;
    } catch {
      // ignore
    }
  }
  return list.filter((r) => isPaOpenQuote(r));
}

async function fetchTravelAgentQuotes(agentId, email) {
  let list = [];
  try {
    const res = await listTravelAgentQuotes(agentId, 0, 50);
    const body = res?.data ?? res;
    const rows = body?.content ?? body?.data?.content ?? body?.data ?? body;
    if (Array.isArray(rows) && rows.length > 0) {
      list = rows;
    }
  } catch {
    list = [];
  }
  if (!list.length && email) {
    try {
      const clientRes = await listTravelClientQuotes({ email });
      if (Array.isArray(clientRes?.data)) list = clientRes.data;
    } catch {
      // ignore
    }
  }
  return list.filter((r) => isTravelOpenQuote(r));
}

export async function fetchAgentQuotes(user, opts = {}) {
  const agnCode = user?.[CUSTOM_AGENT_CODE];
  const email = getUserEmail(user);
  let numericId = opts.agentNumericId;
  let shortDesc = opts.agentShortDesc;

  if (numericId == null && typeof localStorage !== 'undefined') {
    const raw = localStorage.getItem('agentNumericId');
    if (raw) {
      const parsed = parseInt(raw, 10);
      if (!Number.isNaN(parsed)) numericId = parsed;
    }
  }
  if (!shortDesc && typeof localStorage !== 'undefined') {
    shortDesc = localStorage.getItem('agentId');
  }

  if (numericId == null && !shortDesc && (agnCode || email)) {
    const sum = await fetchAgentSummary({ agnCode, email });
    if (sum?.agentNumericId != null) numericId = sum.agentNumericId;
    if (sum?.agentShortDesc) shortDesc = sum.agentShortDesc;
  }

  const primaryAgentId = numericId != null ? String(numericId) : (shortDesc || agnCode || '');

  const [motorRes, paRes, travelRes, health, dp] = await Promise.all([
    primaryAgentId ? fetchMotorAgentQuotes(primaryAgentId) : Promise.resolve([]),
    primaryAgentId ? fetchPaAgentQuotes(primaryAgentId, email) : Promise.resolve([]),
    primaryAgentId ? fetchTravelAgentQuotes(primaryAgentId, email) : Promise.resolve([]),
    fetchHealthQuotes(email),
    fetchDpQuotes(email),
  ]);

  return {
    motor: motorRes,
    pa: mergePendingPaQuote(email, paRes),
    health,
    dp,
    travel: travelRes,
  };
}

function mapQuotesToDashboardActiveQuotes(quotesBundle) {
  const motorQuotes = (quotesBundle?.motor || []).map((q) => {
    const risks = q.details?.risks || [];
    const selectedRisk = risks.find((r) => r.selected === 'Y') || risks[0] || {};
    const rawCover = (selectedRisk.coverType || '').toLowerCase();
    let product = 'Motor';
    if (rawCover.includes('comprehensive')) product = 'Motor Comp.';
    else if (rawCover.includes('third party only') || rawCover.includes('tpo')) product = 'Motor TPO';
    else if (rawCover.includes('fire')) product = 'Motor TPF';
    else if (selectedRisk.coverType) product = selectedRisk.coverType;

    const clientName = q.details?.client?.fullName?.trim() || 'N/A';
    const premium = parseFloat(selectedRisk.premium || '0') || 0;
    const car = selectedRisk.car || {};
    const vehicle = (car.make && car.model)
      ? `${car.make} ${car.model}${car.yearOfManufacture ? ` (${car.yearOfManufacture})` : ''}`
      : null;

    return {
      client: clientName,
      product,
      value: premium,
      quoteId: q.quoteId,
      vehicle,
      type: 'motor',
    };
  });

  const paQuotes = (quotesBundle?.pa || []).map((q) => ({
    client: q.proposerName || 'N/A',
    product: 'Students PA',
    value: parseFloat(q.premium || '0') || 0,
    quoteId: q.quoteId,
    vehicle: q.optionName || null,
    type: 'pa',
  }));

  const travelQuotes = (quotesBundle?.travel || []).map((q) => ({
    client: q.customerEmail || q.proposerName || q.email || 'N/A',
    product: 'Travel',
    value: parseFloat(q.kshAmount || q.premium || '0') || 0,
    quoteId: q.quoteId || q.id,
    vehicle: q.destinationCountry || q.destination || q.region || null,
    type: 'travel',
  }));

  const all = [...motorQuotes, ...paQuotes, ...travelQuotes];
  const totalCount = all.length;
  const totalValue = all.reduce((sum, q) => sum + (q.value || 0), 0);
  return { quotes: all, totalCount, totalValue };
}

async function fetchAgentCommissionsPayable({ user, summary }) {
  const agnCode = summary?.agent?.code != null
    ? String(summary.agent.code)
    : user?.[CUSTOM_AGENT_CODE] || null;
  if (!agnCode) return {};

  try {
    const now = new Date();
    const dateFrom = `01-01-${now.getFullYear()}`;
    const dateTo = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
    const res = await axios.get('/customer/gis/agent/commission-payments', {
      params: {
        agnCode, pAuth: 'P', dateFrom, dateTo, limit: 1000,
      },
      ...silentValidateStatus,
    });
    if (res.status !== 200 || !res.data) return {};

    const { data } = res;
    const obj = data.object ?? data;
    const summaryObj = obj?.summary ?? obj ?? data;
    const netVal = summaryObj.netComm ?? summaryObj.commisNet ?? summaryObj.net_comm ?? summaryObj.totalNetComm
      ?? data.netComm ?? data.commisNet ?? data.net_comm ?? data.totalNetComm ?? 0;
    let total = Number(netVal) || 0;
    let raw = [];
    if (Array.isArray(data.data)) raw = data.data;
    else if (Array.isArray(data)) raw = data;
    else if (Array.isArray(obj?.data)) raw = obj.data;
    else if (Array.isArray(obj?.pageItems)) raw = obj.pageItems;
    else if (Array.isArray(obj)) raw = obj;

    if (raw.length > 0 && total === 0) {
      total = raw.reduce((acc, row) => {
        const n = row.netComm ?? row.commisNet ?? row.commis_net ?? row.net_commission ?? 0;
        return acc + (Number(n) || 0);
      }, 0);
    }
    return { commissionsPayable: total };
  } catch {
    return {};
  }
}
